<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import ComexPipelineStatus from '@/components/base/fluxo/ComexPipelineStatus.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import DocumentoEntradaGrid from '@/components/Compras/documentoEntrada/DocumentoEntradaGrid.vue'
import SolicitacaoAdiantamentoGrid from '@/components/Financeiro/contasPagarReceber/solicitacaoAdiantamento/SolicitacaoAdiantamentoGrid.vue'
import EmbarquesProdutosSmartMode from '@/components/logistica/embarques/EmbarquesProdutosSmartMode.vue'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  contextoPai: {
    type: Object,
    default: () => ({})
  },
  drilldownMode: {
    type: Boolean,
    default: false
  },
  pipelineProps: {
    type: Object,
    default: () => ({})
  },
  funcao: {
    type: String,
    default: 'edicao',
    validator: (v) => ['edicao', 'adicao'].includes(v)
  }
})

const emit = defineEmits([
  'itemAtualizado',
  'cancelar',
  'show-notification',
  'drilldown-pop',
  'pipeline-navegar',
  'drilldown-push',
  'update:funcao'
])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

// ============================================================
// ESTADO
// ============================================================
const idEmbarque = computed(() => props.pipelineProps?.idEmbarque || props.itemParaEditar?.idEmbarque)
const numeroProcessoEmbarque = computed(() => props.itemParaEditar?.numeroProcessoEmbarque || props.pipelineProps?.numeroProcessoEmbarque)
const lstStatusEmbarque = computed(() => props.itemParaEditar?.lstStatusEmbarque || '')
const getTextoLista = (lista, id) => {
  if (!lista) {
    console.warn('[EmbarquesMateriaisForm] getTextoLista: lista é null/undefined para id:', id)
    return id || '-'
  }
  const found = lista.find(item => String(item.idListaClienteItem) === String(id) || String(item.idListaSistemaItem) === String(id))
  if (!found) {
    console.warn('[EmbarquesMateriaisForm] getTextoLista: id não encontrado na lista. id:', id, 'lista tem', lista.length, 'itens. Primeiros ids:', lista.slice(0, 5).map(i => i.idListaClienteItem))
  }
  return found ? found.textoLista : (id || '-')
}

const itensEmbarque = ref([])
const isLoadingItens = ref(false)
const listas = ref({})

// ============================================================
// MODAL LANÇAR DESPESAS
// ============================================================
const showModalDocumentosGrid = ref(false)
const documentoSelecionado = ref(null)
const documentoEntradaGridRef = ref(null)
const isLoadingConcluirDigitacao = ref(false)

// ============================================================
// ESTADO PARA SOLICITAÇÕES DE ADIANTAMENTO
// ============================================================
const showModalSolicitacaoAdiantamento = ref(false)
const solicitacaoAdiantamentoGridRef = ref(null)

// ============================================================
// CARREGAR ITENS DO EMBARQUE (via EmbarqueItems)
// ============================================================
const carregarItensEmbarque = async () => {
  if (!idEmbarque.value) {
    itensEmbarque.value = []
    return
  }

  isLoadingItens.value = true
  try {
    const res = await api.get('/EmbarqueItems', {
      params: { idEmbarque: idEmbarque.value, pageSize: 999 }
    })
    itensEmbarque.value = res.data || []
  } catch (e) {
    console.error('Erro ao carregar itens do embarque:', e)
    emit('show-notification', { message: 'Erro ao carregar itens do embarque.', type: 'error' })
    itensEmbarque.value = []
  } finally {
    isLoadingItens.value = false
  }
}

// ============================================================
// CARREGAR LISTAS (incluindo Produto para unidade de medida)
// ============================================================
const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas([
      'Embarque',
      'DocumentoEntradaItem',
      'Produto'
    ])
    listas.value = result
  } catch (e) {
    console.error('Erro ao carregar listas:', e)
  }
}

// ============================================================
// HANDLERS
// ============================================================
const handleItensAtualizados = () => {
  carregarItensEmbarque()
}

const handleCancel = () => {
  emit('drilldown-pop')
}

const podeEditar = computed(() => {
  // Modo read-only do pipeline (etapa ultrapassada selecionada para visualização):
  // mesmo com permissão COMPLETO, não permite edição nesta etapa.
  if (props.pipelineProps?.readOnly) return false
  // Se o status já passou de LOG_ST_EMBARQUE6 (DigitandoMateriais), é read-only
  if (lstStatusEmbarque.value && lstStatusEmbarque.value !== 'LOG_ST_EMBARQUE6') {
    return false
  }
  if (authStore.isAdminInterno) return true
  return authStore.getPermsDoModulo('IMPORTACAO').some(p =>
    p === 'COMPLETO' || (p.includes('EMBARQUE') && p.includes('COMPLETO'))
  )
})

// ============================================================
// NAVEGAÇÃO VIA PIPELINE (padrão ContratoCompraForm)
// ============================================================
const avancarParaValidacao = () => {
  if (!idEmbarque.value) {
    emit('show-notification', { message: 'Salve o embarque antes de avançar.', type: 'warning' })
    return
  }
  emit('drilldown-push', {
    type: 'validacao',
    id: idEmbarque.value,
    props: {
      itemParaEditar: props.itemParaEditar,
      contextoPai: props.contextoPai
    }
  })
}

const handlePipelineClick = (etapa) => {
  console.log(`[EmbarquesMateriaisForm] handlePipelineClick chamado. etapa.key: "${etapa.key}", idEmbarque: ${idEmbarque.value}, lstStatusEmbarque: ${lstStatusEmbarque.value}`)
  if (etapa.key === 'materiais') {
    // Já está na digitação de materiais, não faz nada
    return
  }
  if (etapa.key === 'validacao') {
    console.log(`[EmbarquesMateriaisForm] handlePipelineClick: chamando avancarParaValidacao(). idEmbarque.value: ${idEmbarque.value}`)
    avancarParaValidacao()
  } else {
    console.log(`[EmbarquesMateriaisForm] handlePipelineClick: emitindo pipeline-navegar para "${etapa.key}"`)
    emit('pipeline-navegar', etapa.key)
  }
}

const concluirDigitacaoMateriais = async () => {
  if (!idEmbarque.value) return

  // Validação preventiva: o embarque precisa ter ao menos 1 item
  if (itensEmbarque.value.length === 0) {
    emit('show-notification', {
      message: 'Não é possível concluir a digitação de materiais: nenhum material foi selecionado. Arraste ao menos 1 produto do painel "Produtos Disponíveis" para os "Materiais Selecionados".',
      type: 'warning'
    })
    return
  }

  isLoadingConcluirDigitacao.value = true
  try {
    // 1. Salvar os dados do formulário primeiro (PUT) para persistir no banco
    //    antes de alterar o status (mesmo padrão do iniciarTransito)
    if (props.itemParaEditar?.idEmbarque) {
      const payload = { ...props.itemParaEditar }
      await api.put(`/Embarques/${idEmbarque.value}`, payload)
    }

    // 2. Chamar API de transição de status
    const res = await api.post(`/Embarques/${idEmbarque.value}/concluir-digitacao-materiais`)
    const embarqueAtualizado = res.data
    emit('show-notification', { message: 'Digitação de materiais concluída!', type: 'success' })
    emit('itemAtualizado', embarqueAtualizado)

    // 3. Navegar para a próxima etapa da pipeline (validação LPCO)
    emit('drilldown-push', {
      type: 'validacao',
      id: idEmbarque.value,
      props: {
        itemParaEditar: embarqueAtualizado,
        contextoPai: props.contextoPai
      }
    })
  } catch (e) {
    const data = e.response?.data
    let msg = ''
    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (e.response?.status === 400) {
      msg = 'Não é possível concluir a digitação: adicione ao menos 1 material ao embarque antes de concluir.'
    } else if (e.response?.status === 404) {
      msg = 'Embarque não encontrado ou serviço indisponível. Verifique se o backend foi reiniciado com as alterações mais recentes.'
    } else {
      msg = 'Erro ao concluir a digitação de materiais. Tente novamente.'
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingConcluirDigitacao.value = false
  }
}

onMounted(async () => {
  await carregarListas()
  if (idEmbarque.value) {
    await carregarItensEmbarque()
  }
})

watch(idEmbarque, (novoId) => {
  if (novoId) {
    carregarItensEmbarque()
  }
})
</script>

<template>
  <div class="embarques-materiais-form-wrapper">
    <FormularioCadastro
      module-name="Embarque"
      :is-loading="isLoadingItens"
      :is-editing="!!idEmbarque"
      :entity-info="{ id: numeroProcessoEmbarque || idEmbarque, name: numeroProcessoEmbarque || 'Nova', nameLabel: 'Nº Processo' }"
      :status="getTextoLista(listas.LstStatusEmbarque, lstStatusEmbarque)"
      :audit-info="props.itemParaEditar"
      :ocultar-salvar="true"
      @cancelar="handleCancel"
      @show-notification="(p) => emit('show-notification', p)"
    >
      <template #info-header>
        <div class="info-item">
          <strong>Nº Doc. Transporte: </strong>
          <span>{{ getTextoLista(listas.LstTipoDocumentoTransporte, props.itemParaEditar?.lstTipoDocumentoTransporte) }} {{ props.itemParaEditar?.numeroDocumentoTransporte || '---' }}</span>
        </div>
        <div class="info-item">
          <strong>Operação Comex: </strong>
          <span>{{ getTextoLista(listas.LstTipoOperacaoComex, props.itemParaEditar?.lstTipoOperacaoComex) || '---' }}</span>
        </div>
      </template>

      <!-- PIPELINE DE STATUS (PROGRESSO) - padrão ContratoCompraForm -->
      <ComexPipelineStatus
        v-if="drilldownMode"
        v-bind="pipelineProps"
        :idEmbarque="idEmbarque"
        :lstStatusEmbarque="lstStatusEmbarque"
        @navegar="handlePipelineClick"
      />

      <!-- BARRA DE AÇÕES: Lançar Despesas + Concluir Digitação + Cancelar Embarque -->
      <!-- Oculta em modo read-only (etapa ultrapassada selecionada para visualização) -->
      <div v-if="drilldownMode && idEmbarque && !pipelineProps?.readOnly" class="d-flex justify-content-between align-items-center gap-2 mb-3">
        <div class="d-flex align-items-center gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-warning fw-bold"
            @click="showModalDocumentosGrid = true"
          >
            <i class="bi bi-receipt me-1"></i> Lançar Despesas
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-info fw-bold"
            @click="showModalSolicitacaoAdiantamento = true"
          >
            <i class="bi bi-cash-coin me-1"></i> Solicitar Adiantamento
          </button>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger fw-bold"
            @click="handleCancel"
          >
            <i class="bi bi-x-lg me-1"></i> Cancelar Embarque
          </button>
          <button
            v-if="podeEditar"
            type="button"
            class="btn btn-sm btn-success fw-bold text-white"
            :disabled="isLoadingConcluirDigitacao"
            @click="concluirDigitacaoMateriais"
          >
            <span v-if="isLoadingConcluirDigitacao" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-check2-circle me-1"></i>
            Concluir Digitação de Materiais
          </button>
        </div>
      </div>

      <!-- SMART MODE DE DIGITAÇÃO (EmbarquesProdutosSmartMode) -->
      <div class="card mb-3 border-warning-subtle">
        <div class="card-header bg-warning-subtle d-flex justify-content-between align-items-center">
          <h5 class="mb-0 text-warning-emphasis fw-bold">
            <i class="bi bi-inboxes me-2"></i>
            Digitação de Materiais
          </h5>
          <span v-if="numeroProcessoEmbarque" class="badge bg-warning bg-opacity-25 text-warning-emphasis border border-warning-subtle">
            {{ numeroProcessoEmbarque }}
          </span>
        </div>
        <div class="card-body p-0">
          <div v-if="isLoadingItens" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-warning me-2"></div>
            Carregando itens do embarque...
          </div>
          <div v-show="!isLoadingItens" class="p-0">
            <EmbarquesProdutosSmartMode
              v-if="idEmbarque && Object.keys(listas).length > 0"
              :key="idEmbarque"
              :id-embarque="idEmbarque"
              :itens-embarque="itensEmbarque"
              :listas="listas"
              :pode-editar="podeEditar"
              @itens-atualizados="handleItensAtualizados"
              @show-notification="(p) => emit('show-notification', p)"
            />
            <div v-else class="text-center py-4 text-muted">
              <i class="bi bi-info-circle me-2"></i>
              Crie ou selecione um embarque para iniciar a digitação.
            </div>
          </div>
        </div>
      </div>
    </FormularioCadastro>

    <!-- MODAL: Lançar Despesas (Documentos de Entrada) -->
    <ModalFormulario
      v-model="showModalDocumentosGrid"
      titulo="Lançar Despesas"
      :ocultar-confirmar="true"
      tamanho="xxl"
      @update:model-value="showModalDocumentosGrid = $event"
    >
      <DocumentoEntradaGrid
        ref="documentoEntradaGridRef"
        :id-embarque="idEmbarque"
        v-model:documento-selecionado="documentoSelecionado"
        :tem-modulo-documento="true"
        :tem-completo="true"
        :tem-visualizacao="true"
        :pode-editar="true"
        :listas="listas"
        :exibir-primeiro-produto="true"
        :id-organizacao="authStore.empresaAtiva"
        :com-smart-mode="true"
        :id-parceiro-negocio-fornecedor="contextoPai?.idParceiroNegocioFornecedor || null"
        contexto-origem="embarque"
        modo-alocacao="livre"
        @show-notification="(p) => emit('show-notification', p)"
      />
    </ModalFormulario>

    <!-- MODAL: Solicitar Adiantamento -->
    <ModalFormulario
      v-model="showModalSolicitacaoAdiantamento"
      titulo="Solicitações de Adiantamento"
      :ocultar-confirmar="true"
      tamanho="xl"
      @update:model-value="showModalSolicitacaoAdiantamento = $event"
    >
      <SolicitacaoAdiantamentoGrid
        ref="solicitacaoAdiantamentoGridRef"
        :id-embarque="idEmbarque"
        :pode-editar="true"
        :pode-visualizar="true"
        :id-organizacao="authStore.empresaAtiva"
        @show-notification="(p) => emit('show-notification', p)"
      />
    </ModalFormulario>
  </div>
</template>

<style scoped>
.embarques-materiais-form-wrapper {
  max-width: 100%;
  overflow-x: hidden;
}
</style>
